import { TelemetryServiceOutgoingEvent } from '@pp/telemetry/types';

import { UserSession } from '../config/UserSession';
import { SessionStorage } from '../storage/session/SessionStorage';
import { Telemetry } from '../telemetry/Telemetry';

import { GtagLogger } from './GtagLogger';
import { Logger } from './Logger';

class CoreTelemetryControllerBase {
  private initialTelemetryLogged: boolean = false;

  private longTaskCount: number = 0;
  private longTaskDuration: number = 0;

  private gameState: string = '';

  setGameState(state: string): void {
    this.gameState = state;
  }

  constructor() {
    // setting up telemetry observers, which will logg events in runtime
    this.setupGenericErrorLogging();
    this.setupPreviousSessionHandlers();
    this.setupLongTaskLogging();
    this.setupSessionLogging();

    // initial logging on app start
    this.logSessionStorageAvailability();
  }

  /*
   * Called when the app is loaded and ready
   * */
  logInitialTelemetry(): void {
    if (this.initialTelemetryLogged) return;
    this.initialTelemetryLogged = true;

    this.logPerformance();
    this.logReady();
    this.logLanguage();
    this.logSession();
    this.logSystemInfo();

    this.logGtag();
  }

  /* Setup */
  private setupGenericErrorLogging(): void {
    window.addEventListener('error', (event) => {
      Logger.logFailure(`${event.error?.name}`, {
        place: 'general',
        additional: event.message,
      });
      Logger.logException(event.error);
    });
  }

  private setupPreviousSessionHandlers(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        SessionStorage.setJson('previousSession', {
          sessionBitMask: '0',
        });
      }
    });

    Telemetry.addEventListener(TelemetryServiceOutgoingEvent.BeforeClose, () => {
      SessionStorage.setJson('previousSession', {
        sessionBitMask: '0',
      });
    });
  }

  private setupLongTaskLogging(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          this.longTaskCount += 1;
          this.longTaskDuration += entry.duration;
        });
      });

      observer.observe({ type: 'longtask', buffered: true });

      Logger.logEvent('performanceLongTaskTiming', {
        additional: 'available',
      });
    } catch (error) {
      Logger.logEvent('performanceLongTaskTiming', {
        additional: 'unavailable',
      });
    }

    Telemetry.addEventListener(TelemetryServiceOutgoingEvent.BeforeClose, () => {
      Logger.logDuration('performanceLongTaskTiming', {
        duration: this.longTaskDuration,
        bandwidth: this.longTaskCount,
      });
    });
  }

  private setupSessionLogging(): void {
    Telemetry.addEventListener(TelemetryServiceOutgoingEvent.BeforeClose, () => {
      Logger.logDuration('session', {
        duration: Math.round(performance.now()),
        additional: this.gameState,
      });
    });
  }

  /* Logging */
  private logSessionStorageAvailability(): void {
    Logger.logEvent('sessionStorage', {
      additional: SessionStorage.isAvailable() ? 'available' : 'unavailable',
    });
  }

  private logPerformance(): void {
    const performanceEntries = performance
      .getEntriesByType('resource')
      .filter((log) => (log as PerformanceResourceTiming).initiatorType == 'fetch');

    let perfMeasured: boolean = false;
    const metricNameRegEx = /\/([\w.]+)\?/;
    performanceEntries.forEach((entry) => {
      const metricName = metricNameRegEx.exec(entry.name)?.[1];

      if (metricName) {
        Logger.logDuration(metricName, {
          duration: Math.round(entry.duration),
        });
      }
      if (!perfMeasured && entry.name.indexOf('/bundle.')) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const transferSize = entry.transferSize;
        const isFromCache = transferSize <= 500;
        Logger.logEvent('loaded_from_cache', {
          loggerName: 'performance',
          additional: '' + isFromCache,
          bandwidth: transferSize,
        });
        perfMeasured = true;
      }
    });
  }

  private logReady(): void {
    const loadingStartContext = {
      standalone: window.parent === window,
      initial: !SessionStorage.get('previousSession'),
    };
    Logger.logDuration('ready', {
      duration: Math.round(performance.now()),
      additional: `${loadingStartContext.initial ? 'initial' : 'refresh'}_${
        loadingStartContext.standalone ? 'standalone' : 'iframe'
      }`,
      instant: true,
    });
  }

  private logSession(): void {
    const userSession = UserSession.getSession();
    if (!userSession) {
      Logger.logFailure('session', {
        place: 'empty',
      });
    }
  }

  private logLanguage(): void {
    const userSession = UserSession.getSession();
    const uiLang = userSession?.lang ?? null;
    Logger.logEvent('language_' + uiLang, {
      additional: navigator.language,
    });
  }

  private logSystemInfo(): void {
    if (window.navigator.hardwareConcurrency) {
      Logger.logEvent('system_core_count', {
        additional: '' + window.navigator.hardwareConcurrency,
      });
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const connection = window.navigator.connection;
    if (connection) {
      if (connection.effectiveType) {
        Logger.logEvent('system_connection_type', {
          additional: connection.effectiveType,
        });
      }
      if (connection.rtt) {
        Logger.logEvent('system_connection_rtt', {
          additional: connection.rtt,
        });
      }
    }
  }

  private logGtag(): void {
    const loadingTimeForGtag = performance.measure('loadingTimeForGtag', 'loadStartForGtag');
    if (loadingTimeForGtag?.duration) {
      GtagLogger.logEvent('game-loaded', loadingTimeForGtag.duration / 1000);
    } else {
      GtagLogger.logEvent('game-loaded', 0);
    }
  }
}

export const CoreTelemetryController = new CoreTelemetryControllerBase();
